import {
	useGetCashbackAdhocClaimResultQuery,
	useGetCashbackAdhocPreviewResultQuery,
	useStartCashbackAdhocClaimMutation,
	useStartCashbackAdhocPreviewMutation,
} from "@api";
import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 120000;
const PENDING_BETS_CODE = "PENDING_UNSETTLED_BET";

const useController = ({ showAlert, getCurrentBalance }) => {
	const [triggerPreview] = useStartCashbackAdhocPreviewMutation();
	const [triggerClaim] = useStartCashbackAdhocClaimMutation();

	const [previewRequestId, setPreviewRequestId] = useState(null);
	const [isPreviewStarting, setIsPreviewStarting] = useState(false);
	const [previewStartError, setPreviewStartError] = useState(null);
	const [isPreviewTimeout, setIsPreviewTimeout] = useState(false);
	const [previewPollInterval, setPreviewPollInterval] = useState(0);

	const [claimRequestId, setClaimRequestId] = useState(null);
	const [claimingId, setClaimingId] = useState(null);
	const [isClaimStarting, setIsClaimStarting] = useState(false);
	const [claimStartError, setClaimStartError] = useState(null);
	const [isClaimTimeout, setIsClaimTimeout] = useState(false);
	const [claimPollInterval, setClaimPollInterval] = useState(0);

	const previewTimeoutRef = useRef(null);
	const claimTimeoutRef = useRef(null);
	const handledClaimRef = useRef({ requestId: null, timeoutHandled: false });

	const previewResult = useGetCashbackAdhocPreviewResultQuery(previewRequestId, {
		skip: !previewRequestId || isPreviewTimeout,
		pollingInterval: previewPollInterval,
	});

	const claimResult = useGetCashbackAdhocClaimResultQuery(claimRequestId, {
		skip: !claimRequestId || isClaimTimeout,
		pollingInterval: claimPollInterval,
	});

	const isPreviewProcessing =
		!!previewRequestId &&
		!isPreviewTimeout &&
		(!previewResult.data || previewResult.data.status === "processing");

	const isClaimProcessing =
		!!claimRequestId &&
		!isClaimTimeout &&
		(!claimResult.data || claimResult.data.status === "processing");

	useEffect(() => {
		if (!previewRequestId || isPreviewTimeout) {
			setPreviewPollInterval(0);
			return;
		}
		if (previewResult.data && previewResult.data.status !== "processing") {
			setPreviewPollInterval(0);
			return;
		}
		setPreviewPollInterval(POLL_INTERVAL_MS);
	}, [previewRequestId, isPreviewTimeout, previewResult.data]);

	useEffect(() => {
		if (!claimRequestId || isClaimTimeout) {
			setClaimPollInterval(0);
			return;
		}
		if (claimResult.data && claimResult.data.status !== "processing") {
			setClaimPollInterval(0);
			return;
		}
		setClaimPollInterval(POLL_INTERVAL_MS);
	}, [claimRequestId, isClaimTimeout, claimResult.data]);

	useEffect(() => {
		if (!isPreviewProcessing) {
			if (previewTimeoutRef.current) {
				clearTimeout(previewTimeoutRef.current);
				previewTimeoutRef.current = null;
			}
			return undefined;
		}
		if (previewTimeoutRef.current) return undefined;
		previewTimeoutRef.current = setTimeout(() => {
			setIsPreviewTimeout(true);
			previewTimeoutRef.current = null;
		}, POLL_TIMEOUT_MS);
		return () => {
			if (previewTimeoutRef.current) {
				clearTimeout(previewTimeoutRef.current);
				previewTimeoutRef.current = null;
			}
		};
	}, [isPreviewProcessing]);

	useEffect(() => {
		if (!isClaimProcessing) {
			if (claimTimeoutRef.current) {
				clearTimeout(claimTimeoutRef.current);
				claimTimeoutRef.current = null;
			}
			return undefined;
		}
		if (claimTimeoutRef.current) return undefined;
		claimTimeoutRef.current = setTimeout(() => {
			setIsClaimTimeout(true);
			claimTimeoutRef.current = null;
		}, POLL_TIMEOUT_MS);
		return () => {
			if (claimTimeoutRef.current) {
				clearTimeout(claimTimeoutRef.current);
				claimTimeoutRef.current = null;
			}
		};
	}, [isClaimProcessing]);

	const startPreview = useCallback(async () => {
		if (previewTimeoutRef.current) {
			clearTimeout(previewTimeoutRef.current);
			previewTimeoutRef.current = null;
		}
		setPreviewStartError(null);
		setIsPreviewTimeout(false);
		setPreviewRequestId(null);
		setIsPreviewStarting(true);
		try {
			const response = await triggerPreview().unwrap();
			if (response?.requestId) {
				setPreviewRequestId(response.requestId);
			} else {
				setPreviewStartError(new Error("MISSING_REQUEST_ID"));
			}
		} catch (error) {
			setPreviewStartError(error);
		} finally {
			setIsPreviewStarting(false);
		}
	}, [triggerPreview]);

	const claim = useCallback(
		async (cashbackId) => {
			if (!cashbackId) return;
			if (claimTimeoutRef.current) {
				clearTimeout(claimTimeoutRef.current);
				claimTimeoutRef.current = null;
			}
			setClaimStartError(null);
			setIsClaimTimeout(false);
			setClaimRequestId(null);
			setClaimingId(cashbackId);
			setIsClaimStarting(true);
			try {
				const response = await triggerClaim({ cashbackId }).unwrap();
				if (response?.requestId) {
					setClaimRequestId(response.requestId);
				} else {
					setClaimStartError(new Error("MISSING_REQUEST_ID"));
				}
			} catch (error) {
				setClaimStartError(error);
			} finally {
				setIsClaimStarting(false);
			}
		},
		[triggerClaim]
	);

	useEffect(() => {
		startPreview();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const completedClaim = claimResult.data?.status === "completed" ? claimResult.data : null;
	const failedClaim = claimResult.data?.status === "failed" ? claimResult.data : null;

	useEffect(() => {
		if (isClaimTimeout) {
			if (!handledClaimRef.current.timeoutHandled) {
				handledClaimRef.current = { requestId: claimRequestId, timeoutHandled: true };
				showAlert({
					type: "error",
					msg: "cashback.adhoc.claimTimeout",
					title: "cashback.adhoc.errorTitle",
				});
			}
			return;
		}

		if (!claimRequestId) return;
		if (handledClaimRef.current.requestId === claimRequestId) return;

		if (completedClaim) {
			handledClaimRef.current = { requestId: claimRequestId, timeoutHandled: false };
			if (completedClaim.isPaymentMade && completedClaim.cashbackAmount > 0) {
				const backendMessage = completedClaim.status || completedClaim.message;
				showAlert(
					backendMessage
						? {
								type: "success",
								msg: backendMessage,
								title: "SUCCESS",
								noTranslate: true,
							}
						: {
								type: "success",
								msg: "cashback.adhoc.claimSuccess",
								title: "cashback.adhoc.claimSuccessTitle",
							}
				);
				getCurrentBalance();
				startPreview();
			} else {
				const backendMessage = completedClaim.message || completedClaim.status;
				showAlert(
					backendMessage
						? {
								type: "error",
								msg: backendMessage,
								title: "ERROR",
								noTranslate: true,
							}
						: {
								type: "error",
								msg: "cashback.adhoc.claimNotPaid",
								title: "cashback.adhoc.claimNotPaidTitle",
							}
				);
				startPreview();
			}
		} else if (failedClaim) {
			handledClaimRef.current = { requestId: claimRequestId, timeoutHandled: false };
			const backendMessage = failedClaim.message;
			showAlert(
				backendMessage
					? {
							type: "error",
							msg: backendMessage,
							title: "ERROR",
							noTranslate: true,
						}
					: {
							type: "error",
							msg: "cashback.adhoc.errorDescription",
							title: "cashback.adhoc.errorTitle",
						}
			);
		}
	}, [
		claimRequestId,
		completedClaim,
		failedClaim,
		isClaimTimeout,
		showAlert,
		getCurrentBalance,
		startPreview,
	]);

	useEffect(() => {
		if (!claimStartError) return;
		const backendMessage = claimStartError?.data?.message || claimStartError?.message;
		showAlert(
			backendMessage
				? {
						type: "error",
						msg: backendMessage,
						title: "ERROR",
						noTranslate: true,
					}
				: {
						type: "error",
						msg: "cashback.adhoc.errorDescription",
						title: "cashback.adhoc.errorTitle",
					}
		);
	}, [claimStartError, showAlert]);

	const previewData = previewResult.data?.status === "completed" ? previewResult.data : null;
	const previewFailed = previewResult.data?.status === "failed" ? previewResult.data : null;

	return {
		state: {
			isLoading: isPreviewStarting || isPreviewProcessing,
			isTimeout: isPreviewTimeout,
			error: previewStartError || previewResult.error || previewFailed,
			cashbacks: previewData?.cashbacks || [],
			blocked: previewData?.blocked === true,
			blockedMessage: previewData?.message,
			isPendingBets: previewData?.code === PENDING_BETS_CODE,
			isClaiming: isClaimStarting || isClaimProcessing,
			claimingId,
		},
		handlers: {
			startPreview,
			claim,
		},
	};
};

export default useController;
